import { useState, useEffect } from 'react';
import { Modal, Button, Form, Input, Select, Radio, DatePicker, message } from 'antd';
import dayjs from 'dayjs';
import './style.less';
import { isEmpty } from 'lodash';
import { saveGuestJourney } from '@services/Guest';
import WorkExperiences from '@/components/common/WorkExperiences';
import RemarksTip from '../RemarksTip';
import { logChangeArrivalCar, logChangeReturnCar } from '@/utils/conferenceLogger';

const { TextArea } = Input;
const { Option } = Select;

// 用车需求类型
const CarRequirementType = {
    AIRPORT_PICKUP: '机场接送',
    STATION_PICKUP: '车站接送',
    OTHER: '其他',
};

// 表单值类型
const CarEditModal = ({
    type,
    open,
    initialData,
    guestInfo,
    journeyInfo, // 新增交通信息
    conferenceId,
    expertId,
    onCancel,
    onSubmit,
}) => {
    const [form] = Form.useForm();
    const [needCar, setNeedCar] = useState(1); // 1: 会务组安排, 2: 自费, 0: 不需要

    // 标题文本
    const titleText = type === 'arrival' ? '编辑来程用车' : '编辑返程用车';

    // 初始化表单数据
    useEffect(() => {
        if (open) {
            if (initialData) {
                // 回显原用车信息
                // 判断是否需要用车
                let needValue = null; // 默认会务组安排
                if (initialData.need === false || initialData.need === 0) {
                    needValue = 0; // 不需要
                }
                if (initialData.need === 2) {
                    needValue = 2; // 自费
                }
                if (initialData.need === 1) {
                    needValue = 1; // 会务组安排
                }

                const formValues = {
                    needCar: needValue,
                    remarks: initialData.remarks || '',
                    car_type_demand: initialData.car_info?.car_type_demand || '',
                };

                setNeedCar(needValue);

                // 根据类型设置不同的字段
                if (type === 'arrival') {
                    // 来程用车字段
                    formValues.departure_datetime = initialData.car_info?.departure_datetime ? dayjs(initialData.car_info.departure_datetime) : null;
                    formValues.departure_place = initialData.car_info?.departure_place || '';
                    formValues.arrival_place = initialData.car_info?.arrival_place || '';
                    formValues.car_type = initialData.car_info?.car_type || '';
                    formValues.car_no = initialData.car_info?.car_no || '';
                    formValues.driver_name = initialData.car_info?.driver_name || '';
                    formValues.driver_phone = initialData.car_info?.driver_phone || '';
                } else {
                    // 返程用车字段
                    formValues.departure_place = initialData.car_info?.departure_place || '';
                    formValues.arrival_place = initialData.car_info?.arrival_place || '';
                    formValues.departure_datetime = initialData.car_info?.departure_datetime ? dayjs(initialData.car_info.departure_datetime) : null;
                    formValues.car_type = initialData.car_info?.car_type || '';
                    formValues.car_no = initialData.car_info?.car_no || '';
                    formValues.driver_name = initialData.car_info?.driver_name || '';
                    formValues.driver_phone = initialData.car_info?.driver_phone || '';
                }

                form.setFieldsValue(formValues);
                setNeedCar(needValue);
            } else {
                // 不设置默认值
                const formValues = {
                    needCar: null,
                    remarks: '',
                    car_type_demand: '',
                };

                // 根据类型设置默认值
                if (type === 'arrival') {
                    formValues.departure_datetime = null;
                    formValues.departure_place = '';
                    formValues.arrival_place = '';
                    formValues.car_type = '';
                    formValues.car_no = '';
                    formValues.driver_name = '';
                    formValues.driver_phone = '';
                } else {
                    formValues.departure_place = '';
                    formValues.arrival_place = '';
                    formValues.departure_datetime = null;
                    formValues.car_type = '';
                    formValues.car_no = '';
                    formValues.driver_name = '';
                    formValues.driver_phone = '';
                }

                form.setFieldsValue(formValues);
                setNeedCar(null);
            }
        }
    }, [open, initialData, form, type]);

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // 处理提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            let formData;

            // 如果选择不需要用车，则只传递 need 标志
            if (values.needCar === 0) {
                formData = {
                    ...initialData, // 保留原用车信息
                    need: 0,
                    remarks: values.remarks,
                };
            } else {
                formData = {
                    need: values.needCar, // 1: 会务组安排, 2: 自费
                    remarks: values.remarks,
                    car_info: {
                        car_type_demand: values.car_type_demand || '',
                        departure_datetime: values.departure_datetime ? values.departure_datetime.format('YYYY-MM-DD HH:mm') : '',
                        departure_place: values.departure_place || '',
                        arrival_place: values.arrival_place || '',
                        car_type: values.car_type || '',
                        car_no: values.car_no || '',
                        driver_name: values.driver_name || '',
                        driver_phone: values.driver_phone || '',
                    },
                };
            }

            // 调用保存接口
            const dataWithTimestamp = {
                ...formData,
                updated_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            };

            const params = {
                conferenceId,
                expertId,
            };

            // 根据类型设置不同的字段
            if (type === 'arrival') {
                params.arrivalCar = JSON.stringify(dataWithTimestamp);
            } else {
                params.returnCar = JSON.stringify(dataWithTimestamp);
            }

            const res = await saveGuestJourney(params);

            if (res.status_code === 200) {
                message.success('保存成功');
                form.resetFields();
                onSubmit && onSubmit(formData);

                // 上报操作日志
                // 只有在变更用车信息时才上报（存在 initialData 且变更了用车信息）
                if (initialData && formData.car_info && formData.need !== 0) {
                    try {
                        const carInfo = {
                            car_type_demand: formData.car_info.car_type_demand || '',
                            departure_datetime: formData.car_info.departure_datetime || '',
                            departure_place: formData.car_info.departure_place || '',
                            arrival_place: formData.car_info.arrival_place || '',
                            car_type: formData.car_info.car_type || '',
                            car_no: formData.car_info.car_no || '',
                            driver_name: formData.car_info.driver_name || '',
                            driver_phone: formData.car_info.driver_phone || '',
                        };

                        const logOptions = {
                            conferenceId,
                            expertId,
                            expertName: guestInfo?.name || '',
                            carInfo,
                            reason: '', // 用车变更没有单独的变更理由字段
                            isSuccess: true,
                        };

                        // 根据类型调用不同的日志函数
                        if (type === 'arrival') {
                            await logChangeArrivalCar(logOptions);
                        } else {
                            await logChangeReturnCar(logOptions);
                        }
                    } catch (logError) {
                        console.error('操作日志上报失败:', logError);
                        // 日志上报失败不影响主流程
                    }
                }
            } else {
                message.error(res.message || '保存失败');
            }
        } catch (error) {
            console.log('保存失败:', error);
            message.error('保存失败');
        }
    };

    // 处理用车需求变更
    const handleNeedCarChange = value => {
        setNeedCar(value);

        // 仅在首次填写时（无initialData或initialData.need为false/0），当选择"会务组安排"或"自费"时，自动填充初始值
        const isFirstFill = !initialData || !initialData.need;

        if (isFirstFill && (value === 1 || value === 2) && journeyInfo && journeyInfo.transport_list && journeyInfo.transport_list.length > 0) {
            const lastTransport = journeyInfo.transport_list[journeyInfo.transport_list.length - 1];

            // 获取当前表单值
            const currentValues = form.getFieldsValue();

            if (type === 'arrival') {
                // 来程用车：自动填充出发时间和出发地点
                const updates = {};

                // 如果抵达时间为空，则填充
                if (!currentValues.departure_datetime && lastTransport.arrival_datetime) {
                    updates.departure_datetime = dayjs(lastTransport.arrival_datetime);
                }

                // 如果抵达地点为空，则填充
                if (!currentValues.departure_place && lastTransport.arrival_place) {
                    updates.departure_place = lastTransport.arrival_place;
                }

                if (Object.keys(updates).length > 0) {
                    form.setFieldsValue(updates);
                }
            } else if (type === 'return') {
                // 返程用车：自动填充抵达地点
                if (!currentValues.arrival_place && lastTransport.departure_place) {
                    form.setFieldsValue({
                        arrival_place: lastTransport.departure_place,
                    });
                }
            }
        }
    };

    return (
        <Modal
            title={titleText}
            open={open}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    取 消
                </Button>,
                <Button key="submit" type="primary" onClick={handleSubmit}>
                    提 交
                </Button>,
            ]}
            width={600}
            className="car-edit-modal"
            style={{ maxHeight: '80vh', overflow: 'hidden' }}
            styles={{ body: { maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' } }}
        >
            {/* 嘉宾基本信息 */}
            {guestInfo && (
                <div className="guest-basic-info">
                    <div className="info-item">
                        <span className="label">姓名：</span>
                        <span className="value">
                            {guestInfo.name || '–'}
                            {(guestInfo.gender === 0 || guestInfo.gender === 1) && <i className={`bi bi-person-fill gender-icon ${guestInfo.gender === 0 ? 'female' : 'male'}`}></i>}
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label">级别：</span>
                        <span className="value line-level">{guestInfo.levelLabel || '–'}</span>
                    </div>
                    <div className="info-item">
                        <span className="label">单位：</span>
                        <span className="value">
                            <WorkExperiences workExperiences={guestInfo.workExperiences} layout="inline" showSerialNumber={false} emptyText="–" />
                        </span>
                    </div>
                </div>
            )}

            <div className="modal-content">
                {/* 系统建议时间 */}
                {guestInfo?.suggestedTime && (
                    <div className="suggested-date-section reduced-margin">
                        <span className="system-tag">系统</span>
                        <span className="date-text">
                            {type === 'arrival' ? '已接受日程最早开始时间' : '已接受日程最晚结束时间'} {guestInfo.suggestedTime}
                        </span>
                    </div>
                )}

                {/* 交通信息卡片 */}
                {journeyInfo && !isEmpty(journeyInfo) && journeyInfo.need !== 0 && journeyInfo.transport_list && journeyInfo.transport_list.length > 0 && (
                    <div className="transport-info-section">
                        <div className="section-header">交通</div>
                        <div className="section-content">
                            {/* 交通列表 */}
                            {journeyInfo.transport_list.map((transport, index) => {
                                const isIssued = transport.ticket_ready === true;
                                const transportTypeText = transport.transport_type === 1 ? '飞机' : '火车';
                                const transportIcon = transport.transport_type === 1 ? 'airplane' : 'train-front';

                                // 判断是否有时间信息
                                const hasDepartureTime = transport.departure_datetime && transport.departure_datetime.trim() !== '';
                                const hasArrivalTime = transport.arrival_datetime && transport.arrival_datetime.trim() !== '';

                                let timeDisplay = '';
                                if (hasDepartureTime && hasArrivalTime) {
                                    // 判断是否同一天
                                    const departureDate = transport.departure_datetime.substring(0, 10);
                                    const arrivalDate = transport.arrival_datetime.substring(0, 10);
                                    const isSameDay = departureDate === arrivalDate;

                                    // 根据是否同一天决定显示格式
                                    timeDisplay = isSameDay ? `${departureDate} ${transport.departure_datetime.substring(11, 16)}～${transport.arrival_datetime.substring(11, 16)}` : `${departureDate} ${transport.departure_datetime.substring(11, 16)}～${arrivalDate} ${transport.arrival_datetime.substring(11, 16)}`;
                                } else if (hasDepartureTime) {
                                    // 只有出发时间
                                    const departureDate = transport.departure_datetime.substring(0, 10);
                                    timeDisplay = `${departureDate} ${transport.departure_datetime.substring(11, 16)}`;
                                } else if (hasArrivalTime) {
                                    // 只有抵达时间
                                    const arrivalDate = transport.arrival_datetime.substring(0, 10);
                                    timeDisplay = `${arrivalDate} ${transport.arrival_datetime.substring(11, 16)}`;
                                }

                                // 判断是否有地点信息
                                const hasDeparturePlace = transport.departure_place && transport.departure_place.trim() !== '';
                                const hasArrivalPlace = transport.arrival_place && transport.arrival_place.trim() !== '';

                                let placeDisplay = '';
                                if (hasDeparturePlace && hasArrivalPlace) {
                                    // 同时有出发地和抵达地：显示箭头连接
                                    placeDisplay = `${transport.departure_place}→${transport.arrival_place}`;
                                } else if (hasDeparturePlace) {
                                    // 只有出发地
                                    placeDisplay = transport.departure_place;
                                } else if (hasArrivalPlace) {
                                    // 只有抵达地
                                    placeDisplay = transport.arrival_place;
                                }

                                return (
                                    <div key={index} className="transport-item">
                                        <i className={`bi bi-${transportIcon} transport-icon`}></i>
                                        <div className="transport-details">
                                            <span className="transport-text">
                                                {transportTypeText} {transport.transport_no} {transport.seat_type} {placeDisplay} {timeDisplay}
                                                <span className={`ticket-status-text ${isIssued ? 'issued' : 'not-issued'}`}>{isIssued ? '已出票' : '未出票'}</span>
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* 表单内容 */}
                <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} className="vehicle-form">
                    {/* 用车需求 */}
                    <Form.Item label={type === 'arrival' ? '来程用车需求' : '返程用车需求'} name="needCar">
                        <Radio.Group onChange={e => handleNeedCarChange(e.target.value)}>
                            <Radio value={1}>会务组安排</Radio>
                            <Radio value={2}>自费</Radio>
                            <Radio value={0}>不需要</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {/* 用车信息表单（仅在选择"会务组安排"或"自费"时显示） */}
                    {!!needCar && (
                        <>
                            {/* 来程用车：需求信息块 */}
                            {type === 'arrival' && (
                                <div className="car-info-block requirement-block">
                                    {/* 车型需求 */}
                                    <Form.Item label="车型需求" name="car_type_demand">
                                        <Input placeholder="填写车型需求" />
                                    </Form.Item>

                                    {/* 出发时间 */}
                                    <Form.Item label="出发时间" name="departure_datetime">
                                        <DatePicker showTime placeholder="选择出发时间" format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
                                    </Form.Item>

                                    {/* 出发地点 */}
                                    <Form.Item label="出发地点" name="departure_place">
                                        <Input placeholder="填写出发地点" />
                                    </Form.Item>

                                    {/* 抵达地点 */}
                                    <Form.Item label="抵达地点" name="arrival_place">
                                        <Input placeholder="填写抵达地点" />
                                    </Form.Item>
                                </div>
                            )}

                            {/* 返程用车：需求信息块 */}
                            {type === 'return' && (
                                <div className="car-info-block requirement-block">
                                    {/* 车型需求 */}
                                    <Form.Item label="车型需求" name="car_type_demand">
                                        <Input placeholder="填写车型需求" />
                                    </Form.Item>

                                    {/* 出发地点 */}
                                    <Form.Item label="出发地点" name="departure_place">
                                        <Input placeholder="填写出发地点" />
                                    </Form.Item>

                                    {/* 抵达地点 */}
                                    <Form.Item label="抵达地点" name="arrival_place">
                                        <Input placeholder="填写抵达地点" />
                                    </Form.Item>
                                </div>
                            )}

                            {/* 来程用车：安排信息块 */}
                            {type === 'arrival' && (
                                <div className="car-info-block arrangement-block">
                                    {/* 安排车型 */}
                                    <Form.Item label="安排车型" name="car_type">
                                        <Input placeholder="填写车型（颜色等）" />
                                    </Form.Item>

                                    {/* 车牌号 */}
                                    <Form.Item label="车牌号" name="car_no">
                                        <Input placeholder="填写车牌号" />
                                    </Form.Item>

                                    {/* 司机姓名 */}
                                    <Form.Item label="联系人姓名" name="driver_name">
                                        <Input placeholder="填写联系人姓名" />
                                    </Form.Item>

                                    {/* 司机电话 */}
                                    <Form.Item label="联系人电话" name="driver_phone">
                                        <Input placeholder="填写联系人电话" />
                                    </Form.Item>
                                </div>
                            )}

                            {/* 返程用车：安排信息块 */}
                            {type === 'return' && (
                                <div className="car-info-block arrangement-block">
                                    {/* 预计出发时间 */}
                                    <Form.Item label="预计出发时间" name="departure_datetime">
                                        <DatePicker showTime placeholder="填写预计出发时间" format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
                                    </Form.Item>

                                    {/* 安排车型 */}
                                    <Form.Item label="安排车型" name="car_type">
                                        <Input placeholder="填写车型（颜色等）" />
                                    </Form.Item>

                                    {/* 车牌号 */}
                                    <Form.Item label="车牌号" name="car_no">
                                        <Input placeholder="填写车牌号" />
                                    </Form.Item>

                                    {/* 司机姓名 */}
                                    <Form.Item label="联系人姓名" name="driver_name">
                                        <Input placeholder="填写联系人姓名" />
                                    </Form.Item>

                                    {/* 司机电话 */}
                                    <Form.Item label="联系人电话" name="driver_phone">
                                        <Input placeholder="填写联系人电话" />
                                    </Form.Item>
                                </div>
                            )}
                        </>
                    )}

                    {/* 备注 - 移到表单最下方 */}
                    <div className="remarks-section">
                        <Form.Item label={type === 'arrival' ? '来程用车备注' : '返程用车备注'} style={{ marginBottom: 0 }}>
                            <Form.Item name="remarks" noStyle>
                                <TextArea placeholder={`填写${type === 'arrival' ? '来程' : '返程'}用车备注信息`} rows={3} />
                            </Form.Item>
                            <RemarksTip />
                        </Form.Item>
                    </div>
                </Form>
            </div>
        </Modal>
    );
};

export default CarEditModal;
